import { ChevronDown } from "lucide-react";

/**
 * The few kit pieces the coach's note lists share (#1221 slices 3 and 4):
 * the card frame, the chip, the 44px native-select box the boards draw for a
 * filter or a sort, the textarea and the navy action. Plain elements and
 * tokens, no `ui/` import, so a redrawn section carries no shipped `Card`,
 * `Select` or `Button`. `CoachGoalsPanel.jsx` keeps its own copies from slice 2
 * and is not touched by this slice.
 */

export const NOTES_FRAME = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  padding: 14,
  minWidth: 0,
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line)",
  borderRadius: "var(--nw-radius-card)",
};

export const ROW_FRAME = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  padding: 12,
  minWidth: 0,
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line)",
  borderRadius: "var(--nw-radius-card)",
};

export const CHIP = {
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nw-navy)",
  background: "var(--nw-navy-tint)",
  padding: "2px 8px",
  borderRadius: "var(--nw-radius-pill)",
  whiteSpace: "nowrap",
};

export const HEADING = { margin: 0, fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 17, color: "var(--nw-ink)" };

const BOX = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  minHeight: 44,
  background: "var(--nw-surface)",
  border: "1px solid",
  borderColor: "var(--nw-line)",
  borderRadius: "var(--nw-radius-control)",
};

const GLYPH = { position: "absolute", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" };

/** One bordered 44px box: an optional leading icon, a native select that fills
 *  it (so the whole box is the tap target) and a chevron. The glyphs take no taps. */
export function SelectBox({ icon: Icon, value, onChange, options, ariaLabel, testId, id }) {
  return (
    <span style={BOX}>
      {Icon && <Icon size={14} color="var(--nw-ink-muted)" strokeWidth={2} aria-hidden="true" style={{ ...GLYPH, left: 10 }} />}
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
        data-testid={testId}
        style={{
          appearance: "none",
          WebkitAppearance: "none",
          minHeight: 44,
          padding: Icon ? "0 30px 0 32px" : "0 30px 0 12px",
          background: "transparent",
          border: 0,
          borderRadius: "var(--nw-radius-control)",
          fontSize: 13,
          color: "var(--nw-ink)",
          fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
          cursor: "pointer",
          maxWidth: "100%",
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown size={14} color="var(--nw-ink-muted)" strokeWidth={2} aria-hidden="true" style={{ ...GLYPH, right: 10 }} />
    </span>
  );
}

export const TEXTAREA = {
  width: "100%",
  boxSizing: "border-box",
  minHeight: 88,
  padding: "10px 12px",
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line-strong)",
  borderRadius: "var(--nw-radius-control)",
  fontSize: 15,
  lineHeight: 1.5,
  color: "var(--nw-ink)",
  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  resize: "vertical",
};

export const NAVY_BTN = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  minHeight: 44,
  padding: "0 16px",
  background: "var(--nw-navy)",
  color: "#FFFFFF",
  border: "none",
  fontWeight: 600,
  fontSize: 14,
  borderRadius: "var(--nw-radius-control)",
  cursor: "pointer",
};

export const LINK_BTN = {
  display: "inline-flex",
  alignItems: "center",
  minHeight: 44,
  minWidth: 44,
  background: "none",
  border: "none",
  padding: 0,
  fontSize: 14,
  fontWeight: 600,
  color: "var(--nw-navy)",
  textDecoration: "underline",
  cursor: "pointer",
};

/** A 44px square icon button, always visible (a phone has no hover). */
export const ICON_BTN = {
  width: 44,
  height: 44,
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--nw-radius-control)",
  border: "1px solid var(--nw-line)",
  background: "var(--nw-surface)",
  cursor: "pointer",
};

/** The loading placeholder: three grey bars, never an empty claim (D53 ruling 2). */
export function ListLoading({ testId }) {
  const bar = { height: 14, borderRadius: 6, background: "var(--nw-line)" };
  return (
    <div
      data-testid={testId}
      aria-busy="true"
      style={{ display: "flex", flexDirection: "column", gap: 12, padding: 16, background: "var(--nw-page)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
    >
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <div style={{ ...bar, width: "55%" }} />
          <div style={{ ...bar, width: "22%" }} />
        </div>
      ))}
    </div>
  );
}

/** An empty list: a tile, a 14px line and an optional muted second line. */
export function EmptyList({ testId, title, hint, icon: Icon }) {
  return (
    <div
      data-testid={testId}
      style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "20px 16px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)", textAlign: "center" }}
    >
      {Icon && (
        <div style={{ width: 44, height: 44, borderRadius: 14, background: "var(--nw-navy-tint)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={20} color="var(--nw-navy)" strokeWidth={2} aria-hidden="true" />
        </div>
      )}
      <div style={{ fontWeight: 600, fontSize: 14, color: "var(--nw-ink)", overflowWrap: "anywhere" }}>{title}</div>
      {hint && <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>{hint}</div>}
    </div>
  );
}
